// Kenyan counties — THE single source (R-032).
//
// The 47-county list and the free-text resolver used to live inside
// src/data/import.js, where only the spreadsheet validator could reach them.
// R-032 asks for a county DROPDOWN wherever a county is entered, and a dropdown
// built from a second copy of the list is how two screens start disagreeing
// about whether "Muranga" is a county. So the vocabulary moved here and
// import.js re-exports it for compatibility — there is exactly one array.
//
// ── THE TOLERANCE RULE (load-bearing) ──────────────────────────────────────
// Cases already in the database carry free text typed over three years of v_20:
// "Nairobi", "Murang'a County", "nakuru", and a handful of genuine misspellings.
// A dropdown that silently coerced those to its nearest canonical entry would
// REWRITE clinical records on a screen the user opened for another reason, and
// the rewrite would be invisible. So:
//
//   * countyOptions() always includes the CURRENT stored value verbatim when it
//     is not canonical, marked as such, and pre-selected. Opening a form and
//     closing it changes nothing.
//   * matchCounty() is only ever used to VALIDATE and to SUGGEST. Nothing in
//     this module writes.
//
// Reports deliberately keep grouping on the raw stored strings (spelling
// variants count separately, and the metric says so) — that is a reporting
// decision recorded in metrics/registry.js, not a contradiction of this module.

import { esc } from '../ui/esc.js';

/** The 47 Kenyan counties, canonical spellings (also the template's Reference sheet). */
export const KENYA_COUNTIES = Object.freeze([
  'Mombasa', 'Kwale', 'Kilifi', 'Tana River', 'Lamu', 'Taita Taveta', 'Garissa', 'Wajir', 'Mandera', 'Marsabit',
  'Isiolo', 'Meru', 'Tharaka-Nithi', 'Embu', 'Kitui', 'Machakos', 'Makueni', 'Nyandarua', 'Nyeri', 'Kirinyaga',
  "Murang'a", 'Kiambu', 'Turkana', 'West Pokot', 'Samburu', 'Trans Nzoia', 'Uasin Gishu', 'Elgeyo Marakwet', 'Nandi',
  'Baringo', 'Laikipia', 'Nakuru', 'Narok', 'Kajiado', 'Kericho', 'Bomet', 'Kakamega', 'Vihiga', 'Bungoma', 'Busia',
  'Siaya', 'Kisumu', 'Homa Bay', 'Migori', 'Kisii', 'Nyamira', 'Nairobi City',
]);

/** Normalise any label for a case-insensitive vocabulary match. */
function norm(v) {
  return String(v === null || v === undefined ? '' : v).trim().toLowerCase();
}

/**
 * Resolve a free-text county to its canonical name (case-insensitive; tolerant
 * of the common "Nairobi" ↔ "Nairobi City" short form and stray punctuation).
 * Returns the canonical county or null when it matches nothing known.
 *
 * VALIDATION/SUGGESTION ONLY — see the tolerance rule above.
 * @param {any} value
 * @returns {string|null}
 */
export function matchCounty(value) {
  const v = norm(value).replace(/\s+county$/, '');
  if (!v) return null;
  for (const c of KENYA_COUNTIES) {
    if (norm(c) === v) return c;
  }
  // Lenient: "nairobi" → "Nairobi City"; "muranga" → "Murang'a". A prefix match is
  // only allowed once the input is long enough to be unambiguous — a 2-3 char
  // fragment like "Ki" would otherwise bind to the FIRST county it prefixes
  // ("Kilifi") and pass validation even though Kiambu/Kisumu/Kitui were as likely.
  const loose = v.replace(/[^a-z]/g, '');
  const MIN_LOOSE = 4;
  for (const c of KENYA_COUNTIES) {
    const cl = norm(c).replace(/[^a-z]/g, '');
    if (cl === loose) return c;
    if (loose.length >= MIN_LOOSE && (cl.startsWith(loose) || loose.startsWith(cl))) return c;
  }
  return null;
}

/**
 * True when `value` is EXACTLY one of the canonical 47 (no leniency). This is
 * the test a dropdown uses to decide whether it must carry the stored value as
 * an extra option — `matchCounty('nairobi')` succeeds, but 'nairobi' is still
 * not a value the canonical <option> list contains.
 * @param {any} value
 * @returns {boolean}
 */
export function isCanonicalCounty(value) {
  return KENYA_COUNTIES.includes(String(value === null || value === undefined ? '' : value));
}

/**
 * The option MODEL for a county select. Pure and DOM-free so it can be tested
 * without a document, and so a non-HTML caller (a native picker, a test) reads
 * the same list.
 *
 * Shape: `{value, label, selected, canonical}`.
 *   - a placeholder is emitted first unless `opts.placeholder` is null;
 *   - a NON-canonical `current` is emitted next, verbatim in `value`, labelled
 *     so the user can see it is not one of the 47 — never rewritten, never
 *     dropped, and selected so that "leave it alone" is the default;
 *   - then the 47, in canonical order.
 * @param {any} [current] the stored/typed value, if any
 * @param {{placeholder?: string|null}} [opts]
 * @returns {Array<{value: string, label: string, selected: boolean, canonical: boolean}>}
 */
export function countyOptions(current, opts) {
  const cur = String(current === null || current === undefined ? '' : current).trim();
  const placeholder =
    opts && 'placeholder' in opts ? opts.placeholder ?? null : 'Select a county…';
  const canonicalCurrent = isCanonicalCounty(cur);
  /** @type {Array<{value: string, label: string, selected: boolean, canonical: boolean}>} */
  const out = [];
  if (placeholder !== null) {
    out.push({ value: '', label: placeholder, selected: !cur, canonical: false });
  }
  if (cur && !canonicalCurrent) {
    // The suggestion is offered in the LABEL only. Selecting this option keeps
    // the stored text byte-for-byte; the user has to pick the canonical entry
    // below to actually change the record.
    const guess = matchCounty(cur);
    out.push({
      value: cur,
      label: guess ? `${cur} (as recorded — did you mean ${guess}?)` : `${cur} (as recorded — not one of the 47)`,
      selected: true,
      canonical: false,
    });
  }
  for (const c of KENYA_COUNTIES) {
    out.push({ value: c, label: c, selected: canonicalCurrent && c === cur, canonical: true });
  }
  return out;
}

/**
 * countyOptions() rendered as `<option>` markup, every value and label esc()'d.
 * The caller supplies the surrounding `<select>` (id, data-action, aria-label)
 * so this helper never guesses at wiring.
 * @param {any} [current]
 * @param {{placeholder?: string|null}} [opts]
 * @returns {string}
 */
export function countyOptionsHTML(current, opts) {
  return countyOptions(current, opts)
    .map((o) => `<option value="${esc(o.value)}"${o.selected ? ' selected' : ''}>${esc(o.label)}</option>`)
    .join('');
}
